/**
 * Schedule generation (Estimate Tool 8.4-8.6, 8.10; Phases E9 and E16).
 *
 * Template in, scaled plan out. Everything here is pure: no database, no clock, no Workamajig.
 * The module that calls it loads the published template and the analogs and hands them over;
 * what comes back is a plan that can be stored, drawn, edited and — at E11 — written.
 *
 * **A template's hours are the template's hours** (8.10, since E16). Until then the generator
 * calibrated every line against the account's history at plan time and flagged the tasks history
 * rarely used. Both moved to the designer, where the person who can act on them sees them: a
 * template whose hours are quietly replaced whenever history disagrees is not maintained by
 * anyone. The generator now scales what it is given to the target and places it in time.
 *
 * Four rules the rest of this file exists to keep.
 *
 * **Hours come from assignments, never from tasks** (8.2). Only 24 % of the account's website
 * tasks carry planned hours while 100 % of their assignments do, so a task's hours here are
 * always the sum of its own assignments and never a number read off the task.
 *
 * **Ordering is an explicit integer and nothing parses a dotted task id** (8.2). Workamajig
 * returns `1.1` for the tenth task of group 1, colliding with the real `1.1`; the raw sequence of
 * the Medium template's first group reads `1.8, 1.9, 1.1, 1.11, 1.12`. Sorting or nesting by that
 * id silently misplaces every task numbered ten or above. Order is `display_order` and parentage
 * is an explicit reference, both established by the sync.
 *
 * **A retired service is re-homed before anything else happens to the line** (8.8). A published
 * template names live services (the designer refuses otherwise), so this is a guard for a service
 * deactivated after a publish: a mapped one re-homes and is reported, an unmapped one stops the
 * generator and names the template and the line.
 *
 * **Nothing here can put a person on an assignment.** There is no parameter for one and no
 * column for one; the shape of an assignment in this file is `{ service_code, hours }`.
 */
import { subtractWeekdays, weekdaysIn } from '../weeks.js';
import { fitToBudget, planAmount, targetHoursForBudget } from './budget.js';
import { rehomeService } from './services.js';
import { weightedMedian } from './price.js';

/**
 * Which generator made a plan. `template-calibrated-scaled-1` calibrated lines against history at
 * plan time (E9-E15); `template-scaled-2` cuts a published template as written (E16).
 */
export const METHOD_VERSION = 'template-scaled-2';

/**
 * Turn a template's plan dates into the two integers the store keeps (Estimate Tool 8.6, 8.10).
 *
 * `ends_before_delivery` is the number of working days between a task's last day and the
 * template's last day of work — 0 for the task that ends the template — and `duration_weekdays`
 * is the inclusive working days the task occupies, never less than one. The end is measured from
 * **leaf** tasks: a curated group row can carry dates that disagree with the work inside it, and a
 * group's own bar is re-derived from its children when the plan is placed anyway.
 *
 * Pure. The import runs it once on the way in; the E13 migration did the same arithmetic in SQL
 * for the versions it converted, and a test pins the two to each other. A task with no dates
 * gets nulls, which `placeDates` lands on the delivery date.
 *
 * @param {{ is_group?: boolean, plan_start?: string|null, plan_complete?: string|null }[]} tasks
 */
export function timingFromDates(tasks) {
  const leaves = tasks.filter((t) => !t.is_group);
  const pool = leaves.length ? leaves : tasks;
  const ends = pool
    .map((t) => t.plan_complete)
    .filter(Boolean)
    .sort();
  const lastEnd = ends[ends.length - 1] ?? null;
  return tasks.map((t) => {
    const { plan_start: start, plan_complete: complete, ...rest } = t;
    if (!lastEnd || !complete || !start) {
      return { ...rest, ends_before_delivery: null, duration_weekdays: null };
    }
    const endsBefore = complete <= lastEnd ? weekdaysIn(complete, lastEnd).length - 1 : 0;
    const duration = start <= complete ? Math.max(1, weekdaysIn(start, complete).length) : 1;
    return { ...rest, ends_before_delivery: endsBefore, duration_weekdays: duration };
  });
}

/**
 * A task name as calibration matches it.
 *
 * The leading outline number has to go: the same task is "6. Programming and Production" in one
 * project and "Programming and Production" in the next, and stripping the number is what lifts
 * that name from 25 % of projects to 52 % (8.4). Everything else is a fold to lower case and a
 * collapse of punctuation and runs of space, so that "Add'l Client Revisions" and "Addl client
 * revisions" are one name.
 *
 * On the account's own data this matches 61 of the Medium template's 62 distinct names to five or
 * more reference projects, at an average of 33.6 references each.
 */
export function normalizeTaskName(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(/^\s*\d+(\.\d+)*[.):\s]+/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Round to an increment, away from zero at the halfway point so rounding is not biased down. */
export function roundTo(value, increment) {
  if (!(increment > 0)) return value;
  const n = value / increment;
  const r = Math.sign(n) * Math.round(Math.abs(n));
  // Integer arithmetic on the multiple, then one division: 0.1 + 0.2 arithmetic on the way out
  // would reintroduce the error the rounding is there to remove.
  return r * increment;
}

/** Sum to the cent, so a float drift cannot make a reconciled total read as unreconciled. */
const sum = (xs) => Math.round(xs.reduce((a, b) => a + b, 0) * 1e6) / 1e6;

/**
 * The tier whose template is nearest the target (8.4).
 *
 * "Nearest" is on a log scale, because the ladder is multiplicative — 168, 433, 979, 1507, 1935 —
 * and on a linear scale a 600-hour target would read as nearer Medium (379 away) than Small (167
 * away) only by accident of where the rungs happen to sit. On a log scale 600 h is 1.39x Small and
 * 0.61x Medium, and Small wins, which is also the answer a producer gives.
 *
 * @param {number} targetHours
 * @param {{ tier: string, hours: number }[]} templates
 */
export function recommendTier(targetHours, templates) {
  const usable = templates.filter((t) => Number(t.hours) > 0);
  if (!usable.length || !(targetHours > 0)) return null;
  let best = null;
  for (const t of usable) {
    const ratio = targetHours / Number(t.hours);
    const distance = Math.abs(Math.log(ratio));
    if (!best || distance < best.distance) {
      best = { tier: t.tier, template_hours: Number(t.hours), scale: ratio, distance };
    }
  }
  return {
    tier: best.tier,
    template_hours: best.template_hours,
    scale: best.scale,
    // Every rung and what it would have cost, so the screen can show the ladder rather than
    // just its answer, and a producer can see the tier they would have picked sitting next to it.
    ladder: usable
      .map((t) => ({
        tier: t.tier,
        template_hours: Number(t.hours),
        scale: targetHours / Number(t.hours),
      }))
      .sort((a, b) => a.template_hours - b.template_hours),
  };
}

/**
 * The target a tier is recommended from: the weighted median hours of the estimate's analog set.
 *
 * The same weights the price used (7.4), so the tier and the number are arguing from the same
 * projects. An analog with no hours is dropped rather than counted as zero — 83 of the account's
 * 202 website projects carry no actual hours, and counting those as zero would drag every
 * recommendation down a rung.
 *
 * @param {{ hours?: number|null, weight: number }[]} analogs
 */
export function targetHoursFromAnalogs(analogs) {
  const items = analogs
    .filter((a) => Number(a.hours) > 0 && Number.isFinite(Number(a.hours)))
    .map((a) => ({ value: Number(a.hours), weight: Math.max(0, Number(a.weight) || 0) }));
  if (!items.length)
    return { hours: null, analogs_with_hours: 0, analogs_considered: analogs.length };
  return {
    hours: weightedMedian(items),
    analogs_with_hours: items.length,
    analogs_considered: analogs.length,
  };
}

export class ScaleOutOfRangeError extends Error {
  /** @param {object} info */
  constructor(info) {
    super(
      `a scale of ${info.scale.toFixed(2)} is outside ${info.scale_min}-${info.scale_max}; ` +
        (info.suggested_tier
          ? `the ${info.suggested_tier} template (${info.suggested_template_hours} h) fits this target`
          : 'no template on the ladder fits this target'),
    );
    this.name = 'ScaleOutOfRangeError';
    this.code = 'SCALE_OUT_OF_RANGE';
    Object.assign(this, info);
  }
}

/**
 * Scale a template to a target and reconcile the rounding exactly (8.5).
 *
 * ```
 * scale      = target_hours / template_hours
 * line_hours = line_hours x scale, rounded to hours_rounding
 * ```
 *
 * Rounding 139 lines to the quarter hour leaves a remainder, and the plan's total has to equal
 * the target the estimator asked for or the reconciliation on the review gate is meaningless. The
 * remainder is settled **on the largest line of each group**, which is the line where a quarter
 * hour is least visible and most defensible.
 *
 * Two lines never move. A line the template gives zero hours stays at zero — group headers,
 * approvals and milestones are structure, not work, and scaling structure produces a fifteen
 * minute client approval. And a group's own row carries no hours of its own; it is the sum of
 * its children.
 *
 * @param {object[]} tasks  template tasks, in display order, re-homed
 * @param {number} targetHours
 * @param {{ hours_rounding: number, scale_min: number, scale_max: number }} settings
 * @param {{ ladder?: object[] }} [context]  used only to name the tier that would have fitted
 */
export function scalePlan(tasks, targetHours, settings, context = {}) {
  // One ratio, since E16: the target over the template's own hours — how much bigger than a
  // Medium this project is. Until then there were two, because calibration replaced each line's
  // hours with a median over only the projects that used that line, so the lines' sum sat near
  // half the template and the multiplier the lines took was not the size ratio the clamp is
  // about. With calibration gone from plan time the two numbers are the same number again.
  const target = roundTo(targetHours, settings.hours_rounding);
  const templateHours = sum(tasks.flatMap((t) => t.assignments.map((a) => a.hours)));
  if (!(templateHours > 0)) {
    return {
      tasks: tasks.map((t) => ({ ...t, hours: 0 })),
      scale: 1,
      total_hours: 0,
      template_hours: 0,
      target_hours: target,
    };
  }
  const scale = target / templateHours;
  if (scale < settings.scale_min || scale > settings.scale_max) {
    // The answer to a scale out of range is a different tier, not a stretched one, and the
    // refusal says which (8.5).
    const fits = (context.ladder ?? [])
      .filter((r) => r.scale >= settings.scale_min && r.scale <= settings.scale_max)
      .sort((a, b) => Math.abs(Math.log(a.scale)) - Math.abs(Math.log(b.scale)))[0];
    throw new ScaleOutOfRangeError({
      scale,
      target_hours: target,
      template_hours: templateHours,
      scale_min: settings.scale_min,
      scale_max: settings.scale_max,
      suggested_tier: fits?.tier ?? null,
      suggested_template_hours: fits?.template_hours ?? null,
    });
  }

  const increment = settings.hours_rounding;
  const scaled = tasks.map((t) => ({
    ...t,
    assignments: t.assignments.map((a) => ({
      ...a,
      // What the template said, kept on the line so the screen can show its working (4.6).
      template_hours: a.template_hours ?? a.hours,
      unrounded_hours: a.hours * scale,
      hours: a.hours > 0 ? roundTo(a.hours * scale, increment) : 0,
    })),
  }));

  // Settle the rounding remainder group by group, on each group's largest line.
  const groups = new Map();
  for (const t of scaled) {
    const key = t.parent_order ?? t.display_order;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }
  for (const members of groups.values()) {
    const lines = members.flatMap((t) => t.assignments.filter((a) => a.hours > 0));
    if (!lines.length) continue;
    const want = roundTo(sum(lines.map((a) => a.unrounded_hours)), increment);
    const have = sum(lines.map((a) => a.hours));
    let remainder = roundTo(want - have, increment);
    if (remainder === 0) continue;
    const largest = lines.reduce((a, b) => (b.hours > a.hours ? b : a));
    // Never below zero: a group whose remainder exceeds its largest line would otherwise get a
    // negative assignment, which is not a thing a schedule can hold.
    largest.hours = Math.max(0, roundTo(largest.hours + remainder, increment));
  }

  // The whole plan's remainder, after the groups have each settled, on the largest line anywhere.
  const allLines = scaled.flatMap((t) => t.assignments.filter((a) => a.hours > 0));
  if (allLines.length) {
    const drift = roundTo(target - sum(allLines.map((a) => a.hours)), increment);
    if (drift !== 0) {
      const largest = allLines.reduce((a, b) => (b.hours > a.hours ? b : a));
      largest.hours = Math.max(0, roundTo(largest.hours + drift, increment));
    }
  }

  const withTotals = scaled.map((t) => ({ ...t, hours: sum(t.assignments.map((a) => a.hours)) }));
  return {
    tasks: withTotals,
    // The size ratio: target over the template's own hours. What the clamp is about, what a
    // screen shows, and — since E16 — the one multiplier every line took.
    scale,
    template_hours: templateHours,
    target_hours: target,
    total_hours: sum(withTotals.flatMap((t) => t.assignments.map((a) => a.hours))),
  };
}

export const ANCHORS = Object.freeze(['delivery', 'span']);

/**
 * The template's own span, in working days. This is the plan's **natural duration**.
 *
 * It is not scaled by the target hours, and that is deliberate. The tier already encodes the
 * size: a Medium website is a Medium website, and its template's span is the curated answer to
 * how long one takes. Scaling duration with hours would say a project half again as big takes
 * half again as long, which is the one thing that is reliably untrue — you add people, not weeks.
 * An estimator who disagrees for this project sets `anchor: 'span'` and gives both ends.
 */
export function templateWeekdays(tasks) {
  const leaves = tasks.filter((t) => !t.is_group);
  const pool = leaves.length ? leaves : tasks;
  let span = 0;
  for (const t of pool) {
    if (t.ends_before_delivery === null || t.ends_before_delivery === undefined) continue;
    const reach = Number(t.ends_before_delivery) + Math.max(1, Number(t.duration_weekdays) || 1);
    if (reach > span) span = reach;
  }
  return span;
}

/**
 * The date work has to begin to deliver on a given date (8.6).
 *
 * Working days, skipping the company holidays the capacity model already holds, so a schedule
 * that crosses Thanksgiving starts earlier rather than quietly losing the week.
 *
 * @param {string} delivery
 * @param {number} weekdays  the span, inclusive: a one-day job starts on the delivery date
 * @param {Set<string>} [holidays]
 */
export function deriveStart(delivery, weekdays, holidays = new Set()) {
  let day = delivery;
  let left = Math.max(1, Math.trunc(weekdays)) - 1;
  // Each holiday inside the window pushes the start one working day earlier: the work still has
  // to fit, and a holiday is a day it cannot be done on.
  for (let guard = 0; left > 0 && guard < 2000; guard++) {
    day = subtractWeekdays(day, 1);
    if (!holidays.has(day)) left--;
  }
  return day;
}

/**
 * Place the plan in time (8.6).
 *
 * **The pass runs backwards, from the delivery date.** A delivery date is a commitment made to a
 * client and a start date usually is not, so the date that must be hit exactly is the end. Placing
 * forwards and stretching toward the end — which is what this did until 2026-09-20 — accumulates
 * rounding in the wrong direction: a plan asked for 2026-10-05 to 2026-12-18 came out ending on
 * 12-21, three days past the commitment, and nothing in the payload said so.
 *
 * Each task's position is the template's own timing — `ends_before_delivery` and
 * `duration_weekdays`, working days measured back from the template's *end* (8.10) — scaled onto
 * the requested window. The task that ends the template therefore ends the plan, on the delivery
 * date, exactly.
 *
 * Two anchors, which differ only in that ratio:
 *
 * - **`delivery`** (the default) keeps the work's natural duration — the template's span — and
 *   pushes it up against the delivery date. The start is derived and reported. This is the mode
 *   that answers "when do we have to begin?", which is the question a delivery date is usually
 *   asked in service of.
 * - **`span`** stretches the work across a start and a delivery that are both fixed, as the
 *   original 8.6 did, and still lands the last task on the delivery date.
 *
 * Tasks land on working days and step over company holidays **backwards**, toward the start:
 * stepping forwards would be the one move that can push a task past the date the whole pass
 * exists to protect.
 *
 * Predecessors are not placed. Inferring dependencies from 3,959 historical predecessor records
 * is a real problem and a separate one: a schedule with correct work and approximate sequencing is
 * useful, and a schedule with invented dependencies is not.
 *
 * @param {object[]} tasks
 * @param {{ start?: string|null, delivery: string, holidays?: Set<string>, anchor?: string }} window
 */
export function placeDates(
  tasks,
  { start = null, delivery, holidays = new Set(), anchor = 'delivery' },
) {
  // The template's own span in working days, from its leaves (8.6, 8.10): the reach of the task
  // that starts earliest, measured back from the template's last day of work.
  const templateSpan = templateWeekdays(tasks);
  // In span mode the work fills the given window. In delivery mode it keeps its own length in
  // days it can actually be worked — so a plan whose window contains nine company holidays
  // occupies nine more working days than the template does, rather than quietly losing them.
  // `deriveStart` counts those back; the window between its answer and the delivery date is what
  // the tasks are then spread across.
  const derived =
    anchor === 'span' && start ? start : deriveStart(delivery, templateSpan, holidays);
  const targetSpan = weekdaysIn(derived, delivery).length;
  // The ratio is over the *gaps* between the ends, not the days at them, so that both ends map
  // exactly: the task at the template's start lands on the window's start and the task at its end
  // lands on the delivery date. Scaling the inclusive counts instead leaves the far end short by
  // a rounding error — which in span mode meant a plan asked to start on the 5th starting on the
  // 2nd, for no reason a reader could see.
  const ratio = templateSpan > 1 && targetSpan > 1 ? (targetSpan - 1) / (templateSpan - 1) : 0;

  // Working days back from the template's end, scaled, then counted back from delivery.
  const place = (offset) => {
    if (offset === null || offset === undefined || ratio === 0) return delivery;
    const backOffset = Math.round(Number(offset) * ratio);
    return skipHolidaysBackwards(subtractWeekdays(delivery, Math.max(0, backOffset)), holidays);
  };

  const placed = tasks.map((t) => {
    const hasTiming = t.ends_before_delivery !== null && t.ends_before_delivery !== undefined;
    const to = place(hasTiming ? t.ends_before_delivery : null);
    let from = place(
      hasTiming
        ? Number(t.ends_before_delivery) + Math.max(1, Number(t.duration_weekdays) || 1) - 1
        : null,
    );
    if (from > to) from = to;
    return { ...t, plan_start: from, plan_complete: to };
  });

  // A group spans its children, so a group's bar on the outline covers the work inside it rather
  // than whatever the 2019 template happened to record on the group row.
  for (const t of placed) {
    if (!t.is_group) continue;
    const children = placed.filter((c) => c.parent_order === t.display_order);
    if (!children.length) continue;
    t.plan_start = children.reduce(
      (a, c) => (c.plan_start < a ? c.plan_start : a),
      children[0].plan_start,
    );
    t.plan_complete = children.reduce(
      (a, c) => (c.plan_complete > a ? c.plan_complete : a),
      children[0].plan_complete,
    );
  }
  return placed;
}

/**
 * The nearest working day at or before `iso` that is not a company holiday.
 *
 * Backwards, always. Stepping forwards off a holiday is the one move that can push a task past
 * the delivery date the backward pass exists to protect.
 */
function skipHolidaysBackwards(iso, holidays) {
  let day = iso;
  for (let guard = 0; guard < 400 && holidays.has(day); guard++) day = subtractWeekdays(day, 1);
  return day;
}

/**
 * The whole generator: a template and its history in, a plan out.
 *
 * @param {{
 *   template: { tier: string, project_number?: string|null, version_id?: string|null,
 *               version_number?: number|null, tasks: object[] },
 *   targetHours?: number|null,
 *   budget?: { amount: number, rates: Map<string, number>|Record<string, number>,
 *              sheet_id?: string|null, sheet_name?: string|null }|null,
 *   window: { start: string, delivery: string, holidays?: Set<string> },
 *   settings: object,
 *   activeServices?: Set<string>|null,
 *   ladder?: object[],
 * }} input
 *
 * `targetHours` and `budget` are the two ways to size the plan (8.5, 8.11). With both, the typed
 * target wins and the budget is only reported against; with a budget alone, the target is what
 * the budget buys at the sheet's rates and the lines are then fitted to the dollar. A `budget`
 * whose `amount` is null is a rate sheet and nothing more: the plan is priced at it, so the
 * screen can show dollars beside every task, and sized from elsewhere.
 */
export function generatePlan(input) {
  const { template, window, settings, activeServices = null, ladder = [], budget = null } = input;

  // 1. Re-home a retired service first, so scaling sees the live code (8.8). A guard since E16:
  //    a published template names live services, but a service can be switched off afterwards.
  const rehomed = [];
  const where = `the ${template.tier} template${template.version_number ? ` (version ${template.version_number})` : ''}`;
  const rehomedTasks = template.tasks.map((task) => ({
    ...task,
    assignments: task.assignments.map((a) => {
      const r = rehomeService(a.service_code, activeServices, {
        where: `${where}, task "${task.name}"`,
      });
      if (r.retired_from) {
        rehomed.push({
          task: task.name,
          from: r.retired_from,
          to: r.code,
          hours: a.hours,
          from_name: r.retirement.from_name,
          to_name: r.retirement.to_name,
        });
      }
      return {
        ...a,
        service_code: r.code,
        service_name: r.retirement?.to_name ?? a.service_name,
        retired_from: r.retired_from,
        retired_from_name: r.retirement?.from_name ?? null,
      };
    }),
  }));

  // 1b. A budget becomes a target (8.11): the template's cost at the sheet, the budget over it,
  //     and that scale applied to the template's hours. Computed on the re-homed lines, because
  //     the rates are keyed by live codes and a retired one has no rate.
  let targetHours = input.targetHours ?? null;
  let fromBudget = null;
  const hasBudget = Boolean(budget) && budget.amount !== null && budget.amount !== undefined;
  if (hasBudget && !(targetHours > 0)) {
    fromBudget = targetHoursForBudget(
      rehomedTasks,
      budget.rates,
      budget.amount,
      settings.hours_rounding,
    );
    targetHours = fromBudget.target_hours;
  }
  if (!(targetHours > 0)) {
    const err = new Error(
      'a plan needs a target: hours, or a budget and a rate sheet to turn into hours',
    );
    err.code = 'NO_TARGET_HOURS';
    throw err;
  }

  // 2. Scale to the target and reconcile the rounding (8.5). No calibration in between: what
  //    the template says is what is scaled (8.10).
  const scaled = scalePlan(
    rehomedTasks.map((t) => ({ ...t, match_key: normalizeTaskName(t.name) })),
    targetHours,
    settings,
    { ladder },
  );

  // 2b. With a budget, the hours are right and the dollars are a few off, because quarter-hour
  //     lines land on a grid of quarter-rates. Fit them: quarter hours move between lines of
  //     different rates until no move brings the plan nearer the budget (8.11). The hours total
  //     does not change, so the plan still reconciles to its target.
  //     A sheet with no budget still prices the plan: the screen shows dollars beside every
  //     task whether or not a budget was set, so the rates are frozen onto every plan.
  let budgetBlock = null;
  if (budget) {
    const fitted = fromBudget
      ? fitToBudget(scaled.tasks, budget.rates, budget.amount, settings.hours_rounding)
      : { moves: [] };
    const priced = planAmount(scaled.tasks, budget.rates);
    budgetBlock = {
      amount: hasBudget ? Number(budget.amount) : null,
      rate_sheet_id: budget.sheet_id ?? null,
      rate_sheet_name: budget.sheet_name ?? null,
      // The rates as they stood, keyed by code: what the plan's dollars are reproducible from.
      rates: budget.rates instanceof Map ? Object.fromEntries(budget.rates) : { ...budget.rates },
      target_from_budget: Boolean(fromBudget),
      template_amount: fromBudget?.template_amount ?? planAmount(rehomedTasks, budget.rates).amount,
      blended_rate: fromBudget?.blended_rate ?? null,
      unrounded_target_hours: fromBudget?.unrounded_target_hours ?? null,
      plan_amount: priced.amount,
      residual: hasBudget ? Math.round((Number(budget.amount) - priced.amount) * 100) / 100 : null,
      unrated: priced.unrated,
      moves: fitted.moves,
    };
  }

  // 3. Place it in time, backwards from the delivery date (8.6).
  const anchor = window.anchor ?? 'delivery';
  const holidays = window.holidays ?? new Set();
  const naturalWeekdays = templateWeekdays(template.tasks);
  const tasks = placeDates(scaled.tasks, { ...window, anchor, holidays });

  // What the plan actually occupies, read off the placed tasks rather than assumed.
  const planStart = tasks.reduce(
    (a, t) => (t.plan_start < a ? t.plan_start : a),
    tasks[0]?.plan_start ?? window.delivery,
  );
  const planEnd = tasks.reduce(
    (a, t) => (t.plan_complete > a ? t.plan_complete : a),
    tasks[0]?.plan_complete ?? window.delivery,
  );
  const requestedStart = window.start ?? null;
  const spanWeekdays = weekdaysIn(planStart, planEnd).length;
  // Working days between the date asked for and the date work must actually begin. Positive is
  // slack — the plan can start later than the estimator expected. Negative is a shortfall, and it
  // is the number that matters when the delivery date has already been given to a client: the
  // work has to begin that many days *before* the date they had in mind. Signed explicitly,
  // because `weekdaysIn` over a reversed range is empty rather than negative.
  const slack = requestedStart
    ? planStart >= requestedStart
      ? weekdaysIn(requestedStart, planStart).length - 1
      : -(weekdaysIn(planStart, requestedStart).length - 1)
    : null;
  const schedule = {
    anchor,
    start_date: planStart,
    delivery_date: window.delivery,
    requested_start_date: requestedStart,
    plan_weekdays: spanWeekdays,
    template_weekdays: naturalWeekdays,
    // Working days between the requested start and the day work must actually begin. Negative
    // means the window is too short: the plan starts before the date that was asked for.
    slack_weekdays: slack,
    starts_before_requested: requestedStart ? planStart < requestedStart : false,
    ends_on_delivery: planEnd === window.delivery,
    holidays_in_window: [...holidays].filter((d) => d >= planStart && d <= planEnd).length,
  };

  const byService = new Map();
  for (const t of tasks)
    for (const a of t.assignments) {
      if (!byService.has(a.service_code))
        byService.set(a.service_code, {
          service_code: a.service_code,
          service_name: a.service_name,
          hours: 0,
        });
      byService.get(a.service_code).hours = sum([byService.get(a.service_code).hours, a.hours]);
    }

  return {
    method_version: METHOD_VERSION,
    tier: template.tier,
    template_project_number: template.project_number ?? null,
    // The version the plan was cut from (8.10). A published version is never mutated, so this
    // is a stable record: when the rung is re-published the plan still points here.
    template_version_id: template.version_id ?? null,
    template_version_number: template.version_number ?? null,
    // What the template assigns, what was asked for (rounded to the increment the schedule is
    // written in), and what the plan totals — the last two equal when the plan reconciles.
    template_hours: scaled.template_hours,
    target_hours: scaled.target_hours,
    requested_hours: targetHours,
    total_hours: scaled.total_hours,
    scale: scaled.scale,
    reconciles: Math.abs(scaled.total_hours - scaled.target_hours) < 1e-6,
    start_date: planStart,
    delivery_date: window.delivery,
    schedule,
    tasks,
    by_service: [...byService.values()].sort(
      (a, b) => b.hours - a.hours || a.service_code.localeCompare(b.service_code),
    ),
    // Named rather than absorbed: 359.5 of the Medium template's 979 hours move service, and the
    // screen has to say so (8.8).
    rehomed_services: rehomed,
    rehomed_hours: sum(rehomed.map((r) => r.hours)),
    // The budget and the dollars, where a budget was given (8.11); null otherwise.
    budget: budgetBlock,
    plan_amount: budgetBlock?.plan_amount ?? null,
  };
}

/**
 * The totals of a plan as it now stands (Estimate Tool 8.7; Phase E10).
 *
 * `generatePlan` returns a plan's totals the moment it is cut. From then on a person edits it —
 * changes hours, changes a service, drops a task, drops a group — and the totals have to follow.
 * This is that recomputation, and it is deliberately the *only* one: the screen, the stored row
 * and the read-back all take their numbers from here, so a group subtotal on screen and the
 * `total_hours` column can never disagree about what a removal meant.
 *
 * Three rules, all of them consequences of how a schedule is written (8.2):
 *
 * - **Hours come from assignments, never from tasks.** A task's hours are the sum of its own
 *   assignments; nothing reads a task's stored figure.
 * - **A group's hours are its descendants', not its own.** Group rows carry no assignments, so
 *   their own sum is zero and their subtotal is the roll-up over every leaf beneath them, at any
 *   depth. Both are returned and they are not interchangeable: the subtotal is what a reader
 *   checks a group against, and the zero is what gets written, because a Workamajig summary task
 *   takes its hours from its children and a group carrying its own would double every one of
 *   them (8.2).
 * - **A removed line contributes nothing, and a line under a removed task contributes nothing
 *   either.** Removal is a flag rather than a deletion (see the E10 migration), so the sum is
 *   what filters, and it has to filter transitively — dropping a group drops its children.
 *
 * @param {{ display_order: number, parent_order: number|null, is_group?: boolean,
 *           removed_by_user?: boolean,
 *           assignments: { service_code: string, service_name?: string|null, hours: number,
 *                          removed_by_user?: boolean }[] }[]} tasks
 * @returns {{ total_hours: number, by_service: object[], own_hours: Map<number, number>,
 *             rollup_hours: Map<number, number>, removed_hours: number, active_tasks: number,
 *             active_assignments: number }}
 */
export function planTotals(tasks) {
  const byOrder = new Map(tasks.map((t) => [t.display_order, t]));
  // A task is live only if nothing above it was removed. Walked with a guard rather than
  // recursively, because a corrupted parent chain must not take the process with it.
  const live = new Map();
  const isLive = (task) => {
    if (live.has(task.display_order)) return live.get(task.display_order);
    let node = task;
    let alive = true;
    for (let guard = 0; guard < 100 && node; guard++) {
      if (node.removed_by_user) {
        alive = false;
        break;
      }
      node = node.parent_order === null ? null : (byOrder.get(node.parent_order) ?? null);
    }
    live.set(task.display_order, alive);
    return alive;
  };

  const byService = new Map();
  /** @type {Map<number, number>} */
  const ownHours = new Map();
  let removed = 0;
  let activeTasks = 0;
  let activeAssignments = 0;

  for (const t of tasks) {
    const alive = isLive(t);
    if (alive && !t.is_group) activeTasks += 1;
    let own = 0;
    for (const a of t.assignments ?? []) {
      const hours = Number(a.hours) || 0;
      if (!alive || a.removed_by_user) {
        removed += hours;
        continue;
      }
      activeAssignments += 1;
      own += hours;
      if (!byService.has(a.service_code)) {
        byService.set(a.service_code, {
          service_code: a.service_code,
          service_name: a.service_name ?? null,
          hours: 0,
          lines: 0,
        });
      }
      const row = byService.get(a.service_code);
      row.hours = sum([row.hours, hours]);
      row.lines += 1;
    }
    ownHours.set(t.display_order, sum([own]));
  }

  // Roll the leaves up into their ancestors. One pass per task rather than a tree walk: the depth
  // is three and the count is 72, and a flat pass cannot recurse into a cycle.
  const rollup = new Map(ownHours);
  for (const t of tasks) {
    const own = ownHours.get(t.display_order) ?? 0;
    if (!own) continue;
    let parent = t.parent_order === null ? null : (byOrder.get(t.parent_order) ?? null);
    for (let guard = 0; guard < 100 && parent; guard++) {
      rollup.set(parent.display_order, sum([rollup.get(parent.display_order) ?? 0, own]));
      parent = parent.parent_order === null ? null : (byOrder.get(parent.parent_order) ?? null);
    }
  }

  return {
    total_hours: sum([...ownHours.values()]),
    removed_hours: sum([removed]),
    active_tasks: activeTasks,
    active_assignments: activeAssignments,
    own_hours: ownHours,
    rollup_hours: rollup,
    by_service: [...byService.values()].sort(
      (a, b) => b.hours - a.hours || a.service_code.localeCompare(b.service_code),
    ),
  };
}
